import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { CompletedEventSummaryModel } from '../../models/completed-event/completed-event-summary.model';
import { TimeService } from '../../services/time.service';
import { LoadingService } from '../../services/loading.service';
import { PersonSummaryModel } from '../../models/person/person-summary.model';
import { TableColumn } from '../../models/table-column.model';
import { CompletedEventMemberService } from '../../services/completed-event-member.service';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { ApprovalRequestExternalCertificateSummaryModel } from 'src/app/models/approval-request/approval-request-external-certificate-summary.model';
import { TrainingCategoryModel } from 'src/app/models/training-category/training-category.model';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { TrainingTypeModel } from 'src/app/models/training/training-type.model';
import { ApprovalRequestService } from 'src/app/services/approval-request.service';
import { TrainingSummaryModel } from 'src/app/models/training/training-summary.model';
import { CourseTemplateSummaryModel } from 'src/app/models/course/course-template-summary.model';
import { EventTypeModel } from 'src/app/models/events/event-type.model';
import { TenantContextService } from 'src/app/services/tenant-context.service';
import { TenantTypeModel } from 'src/app/models/tenants/tenant-type.model';
import { getTrainingTypeLabel } from 'src/app/utils/training-type.utils';

interface ActivityItem {
  id: string;
  type: 'externalRequest' | 'completedEvent' | 'open' | 'upcoming';
  title: string;
  description: string;
  trainingType: TrainingTypeModel;
  date: Date | null;
  iddTimeMinutes?: number;
  trainingCategories: TrainingCategoryModel[];
  trainingGroups: TrainingGroupModel[];
  completedEvent?: CompletedEventSummaryModel;
  externalRequest?: ApprovalRequestExternalCertificateSummaryModel;
}

@Component({
  selector: 'app-completed-event-table',
  templateUrl: './completed-event-table.component.html',
  styleUrls: ['./completed-event-table.component.scss'],
  standalone: false
})
export class CompletedEventTableComponent implements AfterViewInit {
  @Input()
  editingPersonId!: string;

  @Input()
  isCurrentUser!: boolean;
  
  @Input()
  isSuperAdmin!: boolean;

  @Input()
  isBroker!: boolean;

  @Input()
  isExternalCertificateApprovalEnabled!: boolean;

  @Input()
  completedEvents: CompletedEventSummaryModel[] = [];

  @Input()
  externalRequests: ApprovalRequestExternalCertificateSummaryModel[] = [];
  
  @Input()
  openTrainings: TrainingSummaryModel[] = [];

  @Input()
  openCourses: CourseTemplateSummaryModel[] = [];

  @Output()
  refreshEvents = new EventEmitter();

  activityItems: ActivityItem[] = [];

  isEditMode: boolean = false;
  externalCertificatePopupVisible = false;
  approvalRequestCertificatePopupVisible = false;
  reviewExternalCertificatePopupVisible = false;

  currentCertificate: CompletedEventSummaryModel | null = null;
  currentExternalRequest: ApprovalRequestExternalCertificateSummaryModel | null = null;

  disabledButtons = new Set<string>();

  eventsColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.TITLE'),
      dataField: 'title',
      cssClass: 'width-eq-500',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.DATE'),
      dataField: 'date',
      dataType: 'date',
      cellValue: (data: any): any => {
        return new Date(data.date);
      },
      calculateSortValue: (data: ActivityItem): Date | string => {
        if (!data.date) {
          return '';
        }

        return new Date(data.date);
      },
      calculateDisplayValue: (data: ActivityItem): string => {
        if (!data.date) {
          return '';
        }

        return (
          new DatePipe(this.translateService.currentLang).transform(
            new Date(data.date),
            'dd.MM.yyyy'
          ) || ''
        );
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TRAINING-TYPE'),
      dataField: 'trainingType',
      cellValue: (data: ActivityItem): string => {
        return getTrainingTypeLabel(data.trainingType, this.translateService)
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.HOURS'),
      dataField: 'iddTimeMinutes',
      cellValue: (data: ActivityItem): number => {
        return data.iddTimeMinutes ?? 0;
      },
      calculateSortValue: (data: ActivityItem): number => {
        return data.iddTimeMinutes ?? 0;
      },
      calculateDisplayValue: (data: ActivityItem): string => {
        if (!data.iddTimeMinutes) {
          return '';
        }

        return TimeService.getHoursAndMinutesDisplayStringFromIddTimeMinutes(
          data.iddTimeMinutes
        );
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TRAINING-GROUPS'),
      dataField: 'trainingGroups',
      cssClass: 'width-275',
      headerFilter: (data: any) => {
        data.dataSource.postProcess = (results: any) => {
          const trainingGroupNames = results.reduce(
            (a: any, b: any) => a.concat(b.value),
            []
          );
          const parsedTrainingGroupNames: string[] = [];
          trainingGroupNames.forEach((group: string) => {
            if (!group || !group.includes(', ')) {
              parsedTrainingGroupNames.push(group);
            } else if (group.includes(', ')) {
              const groups = group.split(', ');
              parsedTrainingGroupNames.push(...groups);
            }
          });
          const uniqueTrainingGroupNames = [
            ...new Set(parsedTrainingGroupNames),
          ];

          results.splice(0, results.length);
          uniqueTrainingGroupNames.forEach((tgName) => {
            if (tgName) {
              results.push({
                text: tgName,
                value: tgName,
              });
            } else {
              results.push({
                text: '(Blanks)',
                value: null,
              });
            }
          });
          return results;
        };
      },
      filterExpression: (value: any) => {
        function getTrainingGroupsFilterString(rowData: PersonSummaryModel) {
          const groups = rowData.trainingGroups.map((tg) => tg.name);
          return !!groups.join(',') ? groups.join(',') : 'empty';
        }

        return [[getTrainingGroupsFilterString, 'contains', value || 'empty']];
      },
      cellValue: (data: PersonSummaryModel | any): string => {
        return data.trainingGroups.map((tg: any) => tg.name).join(', ');
      },
    },
    // {
    //   caption: this.translateService.instant('COMMON.FIELDS.TRAINING-CATEGORIES'),
    //   dataField: 'trainingCategories',
    //   cssClass: 'width-275',
    //   headerFilter: (data: any) => {
    //     data.dataSource.postProcess = (results: any) => {
    //       const trainingCategoryNames = results.reduce(
    //         (a: any, b: any) => a.concat(b.value),
    //         []
    //       );
    //       const parsedTrainingCategoryNames: string[] = [];
    //       trainingCategoryNames.forEach((category: string) => {
    //         if (!category || !category.includes(', ')) {
    //           parsedTrainingCategoryNames.push(category);
    //         } else if (category.includes(', ')) {
    //           const categories = category.split(', ');
    //           parsedTrainingCategoryNames.push(...categories);
    //         }
    //       });
    //       const uniqueTrainingCategoryNames = [
    //         ...new Set(parsedTrainingCategoryNames),
    //       ];

    //       results.splice(0, results.length);
    //       uniqueTrainingCategoryNames.forEach((tcName) => {
    //         if (tcName) {
    //           results.push({
    //             text: tcName,
    //             value: tcName,
    //           });
    //         } else {
    //           results.push({
    //             text: '(Blanks)',
    //             value: null,
    //           });
    //         }
    //       });
    //       return results;
    //     };
    //   },
    //   filterExpression: (value: any) => {
    //     function getTrainingCategoriesFilterString(rowData: CompletedEventSummaryModel) {
    //       const categories = rowData.trainingCategories.map((tc) => tc.name);
    //       return !!categories.join(',') ? categories.join(',') : 'empty';
    //     }

    //     return [[getTrainingCategoriesFilterString, 'contains', value || 'empty']];
    //   },
    //   cellValue: (data: CompletedEventSummaryModel | any): string => {
    //     return data.trainingCategories.map((tg: any) => tg.name).join(', ');
    //   },
    // },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          icon: null,
          text: this.translateService.instant('COMMON.WELL-ADVISED'),
          isShowAction: (
            data: ActivityItem
          ): boolean => {
            return data.type == 'completedEvent' && !!data.completedEvent && data.completedEvent.eventType === EventTypeModel.GutBeraten;
          },
          onClick: (): void => {},
        },
        {
          icon: null,
          text: this.translateService.instant('COMMON.OPEN-LOWERCASE'),
          isShowAction: (
            data: ActivityItem
          ): boolean => {
            return data.type == 'open';
          },
          onClick: (): void => {},
        },
        {
          icon: null,
          text: this.translateService.instant('COMMON.UPCOMING-LOWERCASE'),
          isShowAction: (
            data: ActivityItem
          ): boolean => {
            return data.type == 'upcoming';
          },
          onClick: (): void => {},
        },
        {
          icon: 'edit',
          isShowAction: (
            data: ActivityItem
          ): boolean => {
            return data.type == 'completedEvent' && !!data.completedEvent 
              && data.completedEvent.eventType === EventTypeModel.External && this.isSuperAdmin;
          },
          onClick: (
            data: ActivityItem
          ): void => {
            this.completedEventSummaryId = data.id;
            this.currentCertificate = this.completedEvents.find(event => event.id == data.id)!;
            this.showEditCerificatePopup();
          },
        },
        {
          icon: 'pdffile',
          isDisabledAction: (
            data: ActivityItem
          ): boolean => {
            if (
              this.disabledButtons.has(
                data.completedEvent!.completedEventMemberId!
              )
            ) {
              return true;
            } else {
              return false;
            }
          },
          isShowAction: (
            data: ActivityItem
          ): boolean => {
            return (
              data.type == 'completedEvent' && !!data.completedEvent && (
                data.completedEvent.eventType === EventTypeModel.Normal ||
                data.completedEvent.eventType === EventTypeModel.Webinar ||
                (data.completedEvent.isExternalCertificateAvailable &&
                  data.completedEvent.eventType == EventTypeModel.External)
              )
            );
          },
          onClick: (
            data: ActivityItem
          ): void => {
            if (
              this.disabledButtons.has(
                data.completedEvent!.completedEventMemberId!
              )
            ) {
              return;
            }
            this.disabledButtons.add(
              data.completedEvent!.completedEventMemberId!
            );
            this.completedEventMemberService.downloadCertificate(
              data.completedEvent!.completedEventMemberId!
            );
            setTimeout(() => {
              this.disabledButtons.delete(
                data.completedEvent!.completedEventMemberId!
              );
            }, 2000);
          },
        },
        {
          icon: 'trash',
          isShowAction: (
            data: ActivityItem
          ): boolean => {
            return data.type == 'completedEvent' && !!data.completedEvent
              && data.completedEvent.eventType === EventTypeModel.External && this.isSuperAdmin;
          },
          onClick: (
            data: ActivityItem
          ): void => {
            this.completedEventSummaryId = data.id;
            this.openDeleteTrainingConfirmationModal();
          },
        },
        {
          icon: 'edit',
          isShowAction: (
            data: ActivityItem
          ): boolean => {
            const isExternalRequest = data.type == 'externalRequest' && !!data.externalRequest;
            return isExternalRequest && this.isCurrentUser;
          },
          onClick: (
            data: ActivityItem
          ): void => {
            this.approvalRequestExternalCertificateId = data.externalRequest!.id;
            this.currentExternalRequest = this.externalRequests.find(item => item.id === data.id)!;
            this.showEditApprovalRequestPopup();
          },
        },
        {
          icon: 'trash',
          isShowAction: (
            data: ActivityItem
          ): boolean => {
            const isExternalRequest = data.type == 'externalRequest' && !!data.externalRequest;

            return isExternalRequest && (
              this.isCurrentUser ||
              (this.isSuperAdmin && !!data.externalRequest?.isDeclined)
            );
          },
          onClick: (
            data: ActivityItem
          ): void => {
            this.approvalRequestExternalCertificateId = data.externalRequest!.id!;
            this.openDeleteApprovalRequestConfirmationModal();
          },
        },
        {
          text: this.translateService.instant('COMMON.REVIEW'),
          isShowAction: (
            data: ActivityItem
          ): boolean => {
            return data.type == 'externalRequest' && 
              !!data.externalRequest && 
              !data.externalRequest.isDeclined && 
              this.isSuperAdmin;
          },
          isButton: true,
           onClick: (
            data: ActivityItem
          ): void => {
            this.approvalRequestExternalCertificateId = data.externalRequest!.id!;
            this.currentExternalRequest = this.externalRequests.find(item => item.id === data.id)!;
            this.showReviewExternalCertificatePopup();
          },
        },
      ],
    },
  ];

  trainingModalOptions: ConfirmationDialogData | null = null;
  showDeleteTrainingModal = false;
  completedEventSummaryId: string | null = null;

  approvalRequestModalOptions: ConfirmationDialogData | null = null;
  showDeleteApprovalRequestModal = false;
  approvalRequestExternalCertificateId: string | null = null;

  isIndividualBrokerTenant: boolean = false;

  public get showAddCertificateButton(): boolean {
    return (
      this.isSuperAdmin ||
      this.isExternalCertificateApprovalEnabled ||
      this.isIndividualBrokerTenant
    );
  }

  constructor(
    private completedEventMemberService: CompletedEventMemberService,
    private approvalRequestService: ApprovalRequestService,
    private translateService: TranslateService,
    private loadingService: LoadingService,
    private tenantContext: TenantContextService,
  ) {}

  ngOnInit() {
    this.isIndividualBrokerTenant = this.tenantContext.isIndividualBrokerTenant();
  }

  ngAfterViewInit() {
    const formattedCompletedEvents: ActivityItem[] = this.completedEvents.map(event => ({
      id: event.id,
      title: event.title,
      description: event.description,
      date: new Date(event.date),
      type: 'completedEvent',
      trainingType: event.trainingType,
      iddTimeMinutes: event.iddTimeMinutes,
      trainingGroups: event.trainingGroups,
      trainingCategories: event.trainingCategories,
      completedEvent: event
    }));

    const inReviewText = this.translateService.instant('COMMON.IN-REVIEW').toLowerCase();
    const declinedText = this.translateService.instant('COMMON.DECLINED');

    const formattedExternalRequests: ActivityItem[] = this.externalRequests.map(req => ({
      id: req.id,
      title: req.isDeclined
        ? declinedText + ' - ' + req.eventName
        : req.eventName + ' - ' + inReviewText,
      date: new Date(req.eventDate),
      type: 'externalRequest',
      description: req.eventDescription,
      trainingType: req.trainingType,
      iddTimeMinutes: req.iddTimeMinutes!,
      trainingCategories: req.trainingCategory ? [req.trainingCategory] : [],
      trainingGroups: [],
      externalRequest: req
    }));

    const now = new Date();
    
    const formattedOpenTrainings: ActivityItem[] = this.openTrainings.map(t => {
      const start = new Date(Math.min(...t.dates.map(d => new Date(d.start).getTime())));
      const end = new Date(Math.max(...t.dates.map(d => new Date(d.end).getTime())));

      const isOpen = now >= start && now <= end;
      
      return {
        id: t.id,
        title: t.name,
        date: start,
        type: isOpen ? 'open' : 'upcoming',
        description: t.description ?? '',
        trainingType: t.trainingType,
        trainingCategories: [],
        trainingGroups: t.trainingGroups
      };
    });

    const formattedOpenCourses: ActivityItem[] = this.openCourses.map(c => ({
      id: c.id,
      title: c.name,
      type: 'open',
      date: null,
      description: c.description ?? '',
      trainingType: c.trainingType,
      trainingCategories: [],
      trainingGroups: c.trainingGroups,
    }));

    const activityItems: ActivityItem[] = [
      ...formattedCompletedEvents,
      ...formattedExternalRequests,
      ...formattedOpenTrainings,
      ...formattedOpenCourses
    ];

    activityItems.sort((a, b) => {
      const aIsOpen = a.type === 'open', bIsOpen  = b.type === 'open';
      // open items come first
      if (aIsOpen !== bIsOpen) {
        return aIsOpen ? -1 : 1;
      }

      const aIsUpcoming  = a.type === 'upcoming', bIsUpcoming  = b.type === 'upcoming';
      // upcoming items next
      if (aIsUpcoming  !== bIsUpcoming) {
        return aIsUpcoming  ? -1 : 1;
      }

      if (!a.date) return 1;
      if (!b.date) return -1;
      return b.date!.getTime() - a.date!.getTime()
    });

    this.activityItems = activityItems;
  }

  rowStyleCallback = (row: ActivityItem) => {
    if (
      row.type === 'open' || 
      row.type === 'upcoming' || 
      row.externalRequest?.isDeclined
    ) {
      return 'gray-row';
    }

    return null;
  };

  onEventCreated() {
    this.externalCertificatePopupVisible = false;
    this.refreshEvents.emit();
  }

  onRequestUpdated(){
    this.approvalRequestCertificatePopupVisible = false;
    this.refreshEvents.emit();
  }

  onRequestReviewed(){
    this.reviewExternalCertificatePopupVisible = false;
    this.refreshEvents.emit();
  }

  onEventRequested() {
    this.refreshEvents.emit();
  }

  showAddCerificatePopup(): void {
    this.isEditMode = false;
    this.externalCertificatePopupVisible = true;
  }

  showEditCerificatePopup(): void {
    this.isEditMode = true;
    this.externalCertificatePopupVisible = true;
  }

  showEditApprovalRequestPopup(): void {
    this.isEditMode = true;
    this.approvalRequestCertificatePopupVisible = true;
  }

  showReviewExternalCertificatePopup(): void {
    this.isEditMode = true;
    this.reviewExternalCertificatePopupVisible = true;
  }

  openDeleteTrainingConfirmationModal() {
    this.trainingModalOptions = {
      submitButtonText: this.translateService.instant('COMMON.YES'),
      message: this.translateService.instant(
        'TRAINING.DELETE-TRAINING-DIALOG.TEXT'
      ),
      dismissButtonText: this.translateService.instant('COMMON.NO'),
    };
    this.showDeleteTrainingModal = true;
  }

  openDeleteApprovalRequestConfirmationModal() {
    this.approvalRequestModalOptions = {
      submitButtonText: this.translateService.instant('COMMON.YES'),
      message: this.translateService.instant(
        'APPROVAL-REQUEST.DELETE-APPROVAL-REQUEST-DIALOG.TEXT'
      ),
      dismissButtonText: this.translateService.instant('COMMON.NO'),
    };
    this.showDeleteApprovalRequestModal = true;
  }

  deleteEvent() {
    if (!this.completedEventSummaryId) return;

    this.loadingService
      .load(
        this.completedEventMemberService.deleteCertificate(
          this.completedEventSummaryId!
        )
      )
      .subscribe(() => {
        this.refreshEvents.emit();
        this.completedEventSummaryId = null;
      });
  }

  deleteApprovalRequestExternalCertificate() {
    if (!this.approvalRequestExternalCertificateId) return;

    this.loadingService
      .load(
        this.approvalRequestService.deleteApprovalRequestExternalCertificate(
          this.approvalRequestExternalCertificateId!
        )
      )
      .subscribe(() => {
        this.refreshEvents.emit();
        this.approvalRequestExternalCertificateId = null;
      });
  }
}
